<div class="header">
  <div class="content">
    <div class="contentInner">
      <div class="pageTitle" appReveal>
        <h1 class="headline">Dash Enterprise</h1>
      </div>
    </div>
  </div>
</div>
<div class="content">
  <div class="contentInner">

    <p class="intro">
      Dash Enterprise centrally manages every Dash display in your organization.  Configure your displays, simplify deployment,
      and learn how your spaces are being used, all from our central web management dashboard.  Dash Enterprise also adds
      a fully-featured web version of Dash, compatible with Smart TVs and commercial displays.
    </p>
    <p class="intro">
      Dash Enterprise supports invoice billing, making it ideal for organizations
      who can't purchase through the App Store or Play Store.
      Get in touch with us to learn more, or to try Dash Enterprise free for one month.
    </p>
    <a class="button" routerLink="/contact">Contact us</a>


    <h2 class="sectionHeader" id="configuration">Central configuration</h2>
    <div class="featureSection">
      <div class="sectionContent">
        <p>
          Change a setting once, and have it apply to every Dash display, whether you have one display or hundreds.
          Configure display modes, booking rules, and branding centrally through our web management dashboard or your MDM system
          (such as Microsoft Intune or Jamf) without having to walk to each device.
        </p>
        <p>
          Combine devices into groups to share configurations, and see a live preview of your configurations before pushing
          them live. Change the configuration and every device updates instantly.
        </p>
        <div class="tip icon">
          <p>Dash Enterprise supports hybrid configurations - set some of your configuration in the web dashboard and some
            in MDM.  Any setting not specified in either place can be set locally on the device.</p>
          <p>For example, add your license key to your MDM configuration to have new devices automatically registered when
          you turn them on, then set your branding and feature preferences via the web dashboard where our live preview
            makes it easy.</p>
        </div>
      </div>
      <app-illustration alt="Dash Enterprise central device configuration in the web dashboard" webpSrc="/assets/images/dash-enterprise-configuration-screenshot-horizontal_2x.webp" src="/assets/images/dash-enterprise-configuration-screenshot-horizontal_2x.png"  width="900" height="675" class="illustration" [cover]="true"></app-illustration>
    </div>

    <h2 class="sectionHeader" id="analytics">Room analytics</h2>
    <div class="featureSection">
      <div class="sectionContent">
        <p>
          Gain insight into how your meeting rooms are being used. Occupancy, booking patterns, and no-show rates are
          broken down with interactive charts.
        </p>
        <p>
          See how your rooms are being booked, spot unused rooms, and find hotspots of meetings that can be moved to times
          with less demand.  Dash Enterprise analytics gives you the tools you need to improve the efficiency of your
          meeting spaces.
        </p>
        <p>
          You can find more information, including details of each chart, in our <a routerLink="/enterprise/analytics" fragment="reports">online guide</a>.
        </p>
      </div>
      <app-illustration alt="Dash Enterprise room analytics in the web dashboard" webpSrc="/assets/images/dash-enterprise-analytics-screenshot-horizontal_2x.webp" src="/assets/images/dash-enterprise-analytics-screenshot-horizontal_2x.png" width="900" height="675" class="illustration" [cover]="true"></app-illustration>
    </div>


    <h2 class="sectionHeader" id="device-management">Advanced device management</h2>
    <div class="featureSection">
      <div class="sectionContent">
        <p>
          See every Dash device, and its current status, from one dashboard. Onboard new displays, deactivate old ones,
          and see if they're up to date.  Group devices to easily manage multiple sites, floors, or room types.
        </p>
        <p>
          Get notified if a device loses power or internet connection so you can fix the problem quickly.
        </p>
        <p>
          Control who can access the dashboard with per-user permissions and two-factor authentication (TOTP), and add
          single sign-on with SAML 2.0 or OpenID Connect to automatically provision users and revoke access as needed.
        </p>
      </div>
      <app-illustration alt="Dash Enterprise device management in the web dashboard" webpSrc="/assets/images/dash-enterprise-devices-screenshot-horizontal_2x.webp" src="/assets/images/dash-enterprise-devices-screenshot-horizontal_2x.png" width="900" height="675" class="illustration" [cover]="true"></app-illustration>
    </div>


    <h2 class="sectionHeader" id="deviceSupport">Extended device support</h2>
    <div class="featureSection">
      <div class="sectionContent">
        <p>
          Dash Enterprise adds support for additional devices.
        </p>
        <p>
          <b>Android without Google Play</b> -  Sideload Dash onto Android devices that don't include the Play Store,
          including dedicated room display panels, and locked-down commercial tablets.  Get a notification each time we
          release a new version so you can keep your devices up to date.
        </p>
        <p>
          <b>Smart TVs and commercial displays</b> - Run the full web version of Dash on any device with a modern browser,
          including smart TVs and commercial displays that don't support our Android or iOS apps. Just create a web
          device in our dashboard and point your device at the URL.
        </p>
        <p>
          You can find detailed information on the devices supported by all versions of Dash in our
          <a routerLink="/hardware">hardware guide</a>.
        </p>
      </div>
      <app-illustration alt="Dash Enterprise running on a smart TV" webpSrc="/assets/images/dash-enterprise-web-screenshot-horizontal.webp" src="/assets/images/dash-enterprise-web-screenshot-horizontal.png" [clickable]="false" width="900" height="675" class="illustration" [cover]="true"></app-illustration>
    </div>

    <p class="intro">
      You can find more detailed information about Dash Enterprise in our <a routerLink="/enterprise">online guide</a>.
      Dash Enterprise offers a low annual per-device cost, and has the option of invoice billing.
      Get in touch with us to learn more, or to try Dash Enterprise free for one month.
    </p>
    <a class="button" routerLink="/contact">Contact us</a>
    <p class="intro">&nbsp;</p>


    <div class="faq">
      <h2 id="faq" class="sectionHeader">Frequently asked questions</h2>
      <h3>What's the difference between Dash Business and Dash Enterprise?</h3>
      <p>Dash Business offers a <a routerLink="/features">fully-featured room display</a>, with room booking, check-in, and branding.  Each display is
        configured individually, on the device itself.</p>
      <p>Dash Enterprise adds central management, allowing you to configure and manage all of your Dash devices from our
        web management dashboard or MDM.  It also offers room analytics and a web version of Dash that runs on any
        device with a modern web browser.</p>

      <h3>How do I purchase Dash Enterprise?</h3>
      <p>Dash Enterprise is sold as an annual subscription through our merchant partner 2Checkout, who support a number
        of convenient payment methods including credit card, wire transfer, and purchase order.  You will receive a
        full tax invoice with your purchase.  <a routerLink="/contact">Contact us</a> for more information, or to
        start a free one-month trial.
      </p>

      <h3>How many meeting rooms do I need for Dash Enterprise?</h3>
      <p>Dash Enterprise has advantages for businesses of any size, in fact we have many customers using Dash Enterprise
        with just one or two meeting rooms.  Dash Enterprise is essential for larger organizations, but also offers a
        number of advantages for small businesses, outlined above.</p>

      <h3>How quickly are my displays updated after I change their configuration in the web dashboard?</h3>
      <p>Dash displays update instantly, so you should see your configuration changes on the devices a few seconds after
        you save the change.</p>

      <h3>Can I use Dash Enterprise on devices that don't support the Play Store or App Store?</h3>
      <p>
        Yes!  Dash Enterprise includes our official APK release of Dash, compatible with Android devices that don't
        include a store.  Get notified with an email when we release a new version, so you always stay up to date.  We
        also offer a web version of Dash, compatible with any device with a modern web browser, such as smart TVs and
        commercial displays.  See our <a routerLink="/hardware">hardware</a> page for more information.
      </p>

      <h3>Does Dash Enterprise support single sign-on?</h3>
      <p>
        The Dash Enterprise web management dashboard supports single sign-on (SSO) via OpenID Connect and SAML 2.0.
        Automatically provision administrator users, set their access, and revoke access when an employee leaves.
        You can find more information in our <a routerLink="/enterprise/user-administration">online guide</a>.
      </p>

      <h3>Is my meeting information private?</h3>
      <p>
        We take the security of your information seriously.  Dash Business is designed so that each Dash display
        communicates with your meeting room calendars directly, and Dash Enterprise doesn't change that.  This means
        that your sensitive information such as meeting titles, descriptions, and attendees never pass through our servers.
      </p>
      <p>
        If you enable room analytics, we collect a small amount of meeting metadata to allow us to generate
        the charts, but this never includes the meeting titles, descriptions, or attendees.  You can find a full list of
        the information collected in our <a routerLink="/enterprise/analytics" fragment="data">analytics guide</a>.
      </p>
    </div>

  </div>
